// The demo booking calendar (Oct 28 2026; replaces Calendly). Open times
// come from Eric's own calendar through the backend (bookingApi.js), shown
// in the visitor's time zone. Wide screens get a week of columns; phones get
// one day at a time, picked from the month calendar. A time opens "Confirm
// your details" (BookingDetails.js); a booking ends on the confirmation.
// Used in the overlay that every "Book a demo" opens (BookingOverlay.js) and
// on the /book-a-demo page (BookingPage.js).
import React, { useEffect, useMemo, useState } from "react";
import ericPhoto from "../../images/founder-avatar-132.webp";
import { CHUNK_MS, HOST_EMAIL, OVERLAP_MS, loadAvailability, loadRange } from "./bookingApi";
import BookingDetails from "./BookingDetails";
import { withRef } from "../referral";
import { addDays, dayKey, detectTimeZone, formatDay, formatTime, keyParts, prefers12h, weekday, zonedToUtc, zoneLongName, zoneOptions } from "./zones";
import { CalendarIcon, CheckIcon, ChevronLeft, ChevronRight, ClockIcon, GlobeIcon, VideoIcon } from "./icons";
import "./booking.css";

const NARROW = "(max-width: 860px)";

// What the demo covers, in the panel's top left (Eric, Oct 28 2026). The
// minutes add up to the 20-minute demo; change them if BOOKING_DURATION_MIN does.
const AGENDA = [
  {
    title: "Your team today",
    minutes: 7,
    points: [
      "How work runs now: your tools, meetings and follow-ups",
      "Where status chasing and escalations cost the most time",
      "The one coordination problem you'd fix first",
    ],
  },
  {
    title: "See BossDrop run",
    minutes: 10,
    points: [
      "Watch it chase an update, escalate a blocker and write the Friday brief",
      "How it works across Slack or Teams, email, calendar and your task tool",
      "Approvals where they matter, every action logged",
    ],
  },
  {
    title: "Fit and next steps",
    minutes: 3,
    points: [
      "Your questions, answered",
      "Whether BossDrop fits your team",
      "If it does, what a 45-day pilot would look like and what we'd measure together",
    ],
  },
];

function useMedia(query) {
  const read = () => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : false);
  const [match, setMatch] = useState(read);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const on = () => setMatch(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, [query]);
  return match;
}

const monthKey = ({ y, mo }) => y * 12 + (mo - 1);
const DAY_MS = 86400000;

export default function BookingFlow({ onClose, titleId = "bk-title" }) {
  const narrow = useMedia(NARROW);
  const [tz, setTz] = useState(detectTimeZone);
  const [hour12, setHour12] = useState(prefers12h);
  const [data, setData] = useState({ status: "loading" });
  const [reload, setReload] = useState(0);
  const [weekStart, setWeekStart] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [month, setMonth] = useState(null);
  const [picked, setPicked] = useState(null);
  const [booked, setBooked] = useState(null);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    const ctrl = new AbortController();
    setData((d) => (d.status === "ready" ? { ...d, refreshing: true } : { status: "loading" }));
    loadAvailability({ signal: ctrl.signal })
      .then((out) => setData(out.ok ? { status: "ready", ...out } : { status: "error", error: out.error }))
      .catch((e) => {
        if (e?.name !== "AbortError") setData({ status: "error", error: "network" });
      });
    return () => ctrl.abort();
  }, [reload]);

  const todayKey = dayKey(Date.now(), tz);
  const slotsByDay = useMemo(() => {
    const map = new Map();
    for (const s of data.slots || []) {
      const key = dayKey(Date.parse(s.start), tz);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }
    return map;
  }, [data.slots, tz]);
  const openDays = useMemo(() => [...slotsByDay.keys()].sort(), [slotsByDay]);
  const lastKey = data.horizonEnd ? dayKey(Date.parse(data.horizonEnd) - 1, tz) : addDays(todayKey, 21);

  const start = weekStart && weekStart >= todayKey ? weekStart : todayKey;
  const day = selectedDay && slotsByDay.has(selectedDay) ? selectedDay : openDays[0] || todayKey;
  const shownMonth = month || keyParts(narrow ? day : start);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const hostName = data.hostName || "Eric Holmes";
  const durationMin = data.durationMin || 20;

  // Later weeks load as the visitor moves toward them: what they can see,
  // plus two weeks so the next click is already there. With nothing open yet,
  // it keeps loading until something is or the horizon is reached.
  const horizonMs = data.horizonEnd ? Date.parse(data.horizonEnd) : 0;
  const loadedTo = data.loadedTo || 0;
  const dayEndMs = (key) => zonedToUtc(keyParts(addDays(key, 1)), tz);
  const isLoaded = (key) => !!data.moreFailed || loadedTo >= Math.min(dayEndMs(key), horizonMs);
  const nextMonth = shownMonth.mo === 12 ? { y: shownMonth.y + 1, mo: 1, d: 1 } : { y: shownMonth.y, mo: shownMonth.mo + 1, d: 1 };
  const viewEnd = Math.max(narrow ? dayEndMs(day) : dayEndMs(weekDays[6]), zonedToUtc(nextMonth, tz));
  const wantTo = openDays.length ? Math.min(viewEnd + 14 * DAY_MS, horizonMs) : horizonMs;
  const needMore = data.status === "ready" && !data.moreFailed && horizonMs > 0 && loadedTo < wantTo;
  const searching = data.status === "ready" && !openDays.length && needMore;

  useEffect(() => {
    if (!needMore) return undefined;
    const ctrl = new AbortController();
    const fromMs = loadedTo - OVERLAP_MS;
    const toMs = Math.min(fromMs + CHUNK_MS, horizonMs);
    loadRange({ fromMs, toMs, signal: ctrl.signal })
      .then((out) =>
        setData((d) => {
          // A reload, or the visitor's zone, moved on while this was loading.
          if (d.status !== "ready" || d.loadedTo !== loadedTo) return d;
          // A failed read, or one that got no further, stops loading; days past it show no times.
          const reached = out.ok ? out.loadedTo || toMs : 0;
          if (reached <= loadedTo) return { ...d, moreFailed: true };
          const seen = new Map(d.slots.map((s) => [s.start, s]));
          for (const s of out.slots) seen.set(s.start, s);
          return { ...d, slots: [...seen.values()].sort((a, b) => Date.parse(a.start) - Date.parse(b.start)), loadedTo: reached };
        }),
      )
      .catch(() => {});
    return () => ctrl.abort();
  }, [needMore, loadedTo, horizonMs]);

  const goWeek = (key) => {
    setWeekStart(key);
    setMonth(keyParts(key));
  };
  const goDay = (key) => {
    setSelectedDay(key);
    setMonth(keyParts(key));
  };
  const pickDay = (key) => (narrow ? goDay(key) : goWeek(key));
  const onTaken = () => {
    setPicked(null);
    setNotice("Someone just booked that time. Please pick another.");
    setReload((n) => n + 1);
  };

  if (booked) {
    return <Booked booked={booked} tz={tz} hour12={hour12} hostName={hostName} onClose={onClose} titleId={titleId} />;
  }

  const dayIndex = openDays.indexOf(day);
  let body;
  if (data.status === "loading" || searching) {
    body = narrow ? <SkeletonDay /> : <SkeletonWeek />;
  } else if (data.status === "error" || !openDays.length) {
    body = <Unavailable empty={data.status !== "error"} onRetry={() => setReload((n) => n + 1)} />;
  } else if (narrow) {
    const list = slotsByDay.get(day) || [];
    body = (
      <div className="bk-daylist">
        <div className="bk-daylist-head">
          <button type="button" className="bk-icon-btn" aria-label="Previous day with times" disabled={dayIndex <= 0} onClick={() => goDay(openDays[dayIndex - 1])}>
            <ChevronLeft />
          </button>
          <h3>{formatDay(day, { weekday: "long", month: "long", day: "numeric" })}</h3>
          <button
            type="button"
            className="bk-icon-btn"
            aria-label="Next day with times"
            disabled={dayIndex < 0 || dayIndex >= openDays.length - 1}
            onClick={() => goDay(openDays[dayIndex + 1])}
          >
            <ChevronRight />
          </button>
        </div>
        <div className="bk-daylist-slots">
          {list.map((s) => (
            <SlotButton key={s.start} slot={s} tz={tz} hour12={hour12} dayLabel={formatDay(day, { weekday: "long", month: "long", day: "numeric" })} onPick={setPicked} />
          ))}
        </div>
      </div>
    );
  } else {
    body = (
      <div className="bk-week-wrap">
      <div className="bk-week">
        {weekDays.map((key) => {
          const list = slotsByDay.get(key) || [];
          const label = formatDay(key, { weekday: "long", month: "long", day: "numeric" });
          const loaded = isLoaded(key);
          return (
            <div className={`bk-col${list.length || !loaded ? "" : " is-empty"}`} key={key} role="group" aria-label={label} aria-busy={!loaded}>
              <div className={`bk-col-head${key === todayKey ? " is-today" : ""}`}>
                <span className="bk-dow">{formatDay(key, { weekday: "short" })}</span>
                <span className="bk-dnum">{keyParts(key).d}</span>
              </div>
              <div className="bk-col-slots">
                {!loaded ? (
                  Array.from({ length: 4 }, (_, j) => <span className="bk-skel" key={j} />)
                ) : list.length ? (
                  list.map((s) => <SlotButton key={s.start} slot={s} tz={tz} hour12={hour12} dayLabel={label} onPick={setPicked} />)
                ) : (
                  <span className="bk-none">No times</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      </div>
    );
  }

  const ready = data.status === "ready" && openDays.length > 0;
  // Nothing to pick from: no range, clock switch or zone note over the email fallback.
  const fallback = data.status === "error" || (data.status === "ready" && !openDays.length);
  return (
    <div className="bk">
      <aside className="bk-side">
        <div className="bk-host">
          <img className="bk-avatar" src={ericPhoto} alt="" width="44" height="44" />
          <span>{hostName}</span>
        </div>
        <h2 className="bk-title" id={titleId}>
          BossDrop demo
        </h2>
        {/* Scrolls inside the space the old description had, so the panel keeps its size. */}
        <div className="bk-agenda" tabIndex={0} role="region" aria-label="What we'll cover">
          <p className="bk-agenda-title">What we'll cover</p>
          <ol className="bk-agenda-list">
            {AGENDA.map((part) => (
              <li key={part.title}>
                <strong>
                  {part.title} <span>({part.minutes} min)</span>
                </strong>
                <ul>
                  {part.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
          <p className="bk-agenda-more">
            Learn more:{" "}
            <a href="/how-it-works" target="_blank" rel="noopener noreferrer">
              How BossDrop works
              <span className="sr-only"> (opens in a new tab)</span>
            </a>
          </p>
        </div>
        <ul className="bk-meta">
          {/* One row for both, so the agenda above gets the room. */}
          <li className="bk-meta-pair">
            <span>
              <ClockIcon /> {durationMin} minutes
            </span>
            <span>
              <VideoIcon /> Google Meet
            </span>
          </li>
          <li className="bk-meta-tz">
            <GlobeIcon />
            <label className="sr-only" htmlFor="bk-tz">
              Time zone
            </label>
            <select id="bk-tz" value={tz} onChange={(e) => setTz(e.target.value)}>
              {zoneOptions(tz).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </li>
        </ul>
        {ready ? (
          <MonthCalendar
            month={shownMonth}
            todayKey={todayKey}
            lastKey={lastKey}
            slotsByDay={slotsByDay}
            isLoaded={isLoaded}
            highlight={narrow ? [day] : weekDays}
            onMonth={setMonth}
            onPick={pickDay}
          />
        ) : null}
      </aside>

      <section className="bk-main" aria-label="Pick a time">
        <div className="bk-main-head">
          <h3 className="bk-range">{narrow || fallback ? "Pick a time" : rangeLabel(weekDays[0], weekDays[6])}</h3>
          <div className="bk-head-tools" hidden={fallback}>
            <div className="bk-seg" role="group" aria-label="Clock format">
              <button type="button" aria-pressed={hour12} onClick={() => setHour12(true)}>
                12h
              </button>
              <button type="button" aria-pressed={!hour12} onClick={() => setHour12(false)}>
                24h
              </button>
            </div>
            {!narrow && ready ? (
              <div className="bk-arrows">
                <button type="button" className="bk-icon-btn" aria-label="Previous week" disabled={start <= todayKey} onClick={() => goWeek(maxKey(todayKey, addDays(start, -7)))}>
                  <ChevronLeft />
                </button>
                <button type="button" className="bk-icon-btn" aria-label="Next week" disabled={addDays(start, 7) > lastKey} onClick={() => goWeek(addDays(start, 7))}>
                  <ChevronRight />
                </button>
              </div>
            ) : null}
          </div>
        </div>
        {notice ? (
          <p className="bk-notice" role="status">
            {notice}
          </p>
        ) : null}
        {body}
        {fallback ? null : <p className="bk-foot">Times shown in {zoneLongName(tz)}.</p>}
      </section>

      {picked ? (
        <BookingDetails
          slot={picked}
          tz={tz}
          hour12={hour12}
          onBack={() => setPicked(null)}
          onTaken={onTaken}
          onBooked={(result) => {
            setPicked(null);
            setBooked(result);
          }}
        />
      ) : null}
    </div>
  );
}

const maxKey = (a, b) => (a > b ? a : b);

function rangeLabel(first, last) {
  const a = keyParts(first);
  const b = keyParts(last);
  const sameYear = a.y === b.y;
  const left = formatDay(first, { month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }) });
  const right = formatDay(last, { month: "short", day: "numeric", year: "numeric" });
  return `${left} – ${right}`;
}

function SlotButton({ slot, tz, hour12, dayLabel, onPick }) {
  const time = formatTime(Date.parse(slot.start), tz, hour12);
  return (
    <button type="button" className="bk-slot" onClick={() => onPick(slot)} aria-label={`${dayLabel}, ${time}`}>
      {time}
    </button>
  );
}

function MonthCalendar({ month, todayKey, lastKey, slotsByDay, isLoaded, highlight, onMonth, onPick }) {
  const { y, mo } = month;
  const first = `${y}-${String(mo).padStart(2, "0")}-01`;
  const days = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  const lead = weekday(first);
  const cells = [...Array(lead).fill(null), ...Array.from({ length: days }, (_, i) => addDays(first, i))];
  const lit = new Set(highlight);
  const shift = (n) => {
    const t = new Date(Date.UTC(y, mo - 1 + n, 1));
    onMonth({ y: t.getUTCFullYear(), mo: t.getUTCMonth() + 1 });
  };
  const atStart = monthKey(month) <= monthKey(keyParts(todayKey));
  const atEnd = monthKey(month) >= monthKey(keyParts(lastKey));
  return (
    <div className="bk-month">
      <div className="bk-month-head">
        <span>{formatDay(first, { month: "long", year: "numeric" })}</span>
        <div className="bk-arrows">
          <button type="button" className="bk-icon-btn" aria-label="Previous month" disabled={atStart} onClick={() => shift(-1)}>
            <ChevronLeft size={16} />
          </button>
          <button type="button" className="bk-icon-btn" aria-label="Next month" disabled={atEnd} onClick={() => shift(1)}>
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
      <div className="bk-month-grid">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <span className="bk-month-dow" key={i} aria-hidden="true">
            {d}
          </span>
        ))}
        {cells.map((key, i) => {
          if (!key) return <span key={`blank-${i}`} />;
          const open = slotsByDay.has(key);
          // Not loaded yet: shown as pending, not as "no times".
          const pending = !open && key >= todayKey && key <= lastKey && !isLoaded(key);
          const cls = ["bk-month-day", open ? "is-open" : "", pending ? "is-pending" : "", lit.has(key) ? "is-lit" : "", key === todayKey ? "is-today" : ""]
            .filter(Boolean)
            .join(" ");
          return (
            <button
              type="button"
              key={key}
              className={cls}
              disabled={!open}
              aria-pressed={lit.has(key)}
              aria-label={`${formatDay(key, { weekday: "long", month: "long", day: "numeric" })}${open ? "" : pending ? ", loading times" : ", no times"}`}
              onClick={() => onPick(key)}
            >
              {keyParts(key).d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SkeletonWeek() {
  return (
    <div className="bk-week-wrap">
      <div className="bk-week" aria-busy="true" aria-label="Loading times">
        {Array.from({ length: 7 }, (_, i) => (
          <div className="bk-col" key={i}>
            <div className="bk-col-head">
              <span className="bk-skel bk-skel-head" />
            </div>
            <div className="bk-col-slots">
              {Array.from({ length: 6 }, (_, j) => (
                <span className="bk-skel" key={j} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SkeletonDay() {
  return (
    <div className="bk-daylist" aria-busy="true" aria-label="Loading times">
      <div className="bk-daylist-slots">
        {Array.from({ length: 8 }, (_, j) => (
          <span className="bk-skel" key={j} />
        ))}
      </div>
    </div>
  );
}

function Unavailable({ empty, onRetry }) {
  // With booking down, this email is the lead, so it carries a specialist's code too.
  const mailto = `mailto:${HOST_EMAIL}?subject=${encodeURIComponent(withRef("BossDrop demo"))}`;
  return (
    <div className="bk-empty">
      <CalendarIcon size={28} />
      <h3>{empty ? "No open times right now" : "Online booking is down right now"}</h3>
      <p>
        Email Eric at <a href={mailto}>{HOST_EMAIL}</a> to find a time.
      </p>
      <div className="bk-empty-actions">
        <a className="bd-button" href={mailto}>
          Email Eric
        </a>
        {empty ? null : (
          <button type="button" className="bk-ghost" onClick={onRetry}>
            Try again
          </button>
        )}
      </div>
    </div>
  );
}

function Booked({ booked, tz, hour12, hostName, onClose, titleId }) {
  const startMs = Date.parse(booked.start);
  const endMs = Date.parse(booked.end);
  const day = formatDay(dayKey(startMs, tz), { weekday: "long", month: "long", day: "numeric" });
  const link = booked.meetLink || "";
  return (
    <div className="bk-done" role="status">
      <div className="bk-done-icon">
        <CheckIcon size={30} />
      </div>
      <h2 className="bk-done-title" id={titleId}>
        You're booked with {hostName.split(" ")[0]}
      </h2>
      <p className="bk-done-when">
        {day}, {formatTime(startMs, tz, hour12)} to {formatTime(endMs, tz, hour12)}
        <span>{zoneLongName(tz, startMs)}</span>
      </p>
      {link ? (
        <a className="bk-done-meet" href={link} target="_blank" rel="noopener noreferrer">
          <VideoIcon /> {link.replace(/^https:\/\//, "")}
          <span className="sr-only"> (Google Meet, opens in a new tab)</span>
        </a>
      ) : null}
      <p className="bk-done-note">
        {booked.inviteSent
          ? `The calendar invite is on its way to ${booked.email} from ${HOST_EMAIL}.`
          : `The invite email didn't go out, so save this link. Eric has your booking and will follow up from ${HOST_EMAIL}.`}
      </p>
      {onClose ? (
        <button type="button" className="bk-ghost" onClick={onClose}>
          Done
        </button>
      ) : null}
    </div>
  );
}
